// REQ-261 — one formatter for every precision-by-context row (AC2), locale-aware through `Intl` (AC1, AC6),
// shared by web and mobile (AC5). Public module (ARCH §8.1 `src/format`, §8.5): apps import it; it imports
// only `src/num` and `src/periods`. Pinned by `tests/golden/formatting.json` (181 cases — 164 `format`, 6 `formatTicks`,
// 11 `parseMoneyInput` — exact equality; the suite is vitest-only and declares so, D-1234).
//
// Design:
//   1. Components name a CONTEXT (`FormatContext`) and never pass digits. The context table below maps each
//      context to a precision rule (how many decimals, which threshold) and an `Intl` recipe.
//   2. Rounding happens ONCE, here, with `roundHalfAway` (ENGINE §1 N-0), on the unrounded value. `Intl` is
//      then given a value that is already exact at the display precision, so its own rounding (half-even in
//      some engines, half-expand in others, and always on the binary double) never decides a trap case:
//      `38%` from 38.499…, `$37.53` from 37.533333, `$1,000.00` from 999.995, `$1M` from 999,950.
//   3. Every string leaves through `formatToParts` + `joinParts`, which normalises per QA's T-067 list:
//      the minus sign is U+2212 (ICU's en-US minus is ASCII `-`), U+2009 / U+202F become an ordinary space
//      (ICU emits them in ranges and before am/pm depending on its version — Node 20 ≠ Chrome ≠ Hermes),
//      the day period is lower-cased ("4:40 pm", REQ-261 AC6's literal) and en's compact `K` is `k`.
//      U+00A0 stays where the locale puts it (`1.092,00 $`). No formatted string is ever edited.
//   4. Time zones: an ISO calendar date (`date.*`) is ALREADY a day in the ad account's zone (ENGINE §2 —
//      `accountToday` made it so) and is rendered as that day without travelling through any zone
//      (`Date.UTC` + `timeZone: 'UTC'`; `new Date('2026-03-08')` is the 7th in Chicago). An instant
//      (`time.clock`) is rendered in the DEVICE zone, which is a parameter — never read from the environment
//      (D-020's spirit: the apps pass `Intl.DateTimeFormat().resolvedOptions().timeZone` once).
//   5. `null` → "—" in every context; the change sentinel `"new"` → "new" (the localised word, when one
//      exists, is the message catalogue's — T-020). The formatter never adds "≈", "about", "per day", arrows
//      or direction words: those are the component's or the sentence's.

import { roundHalfAway, shiftExponent } from '../num/num';
import { parseIsoDate } from '../periods/isoDate';
import type { DateRange } from '../periods/periods';
import type { IsoDate, IsoInstant } from '../types/primitives';

/** Rendered for a missing value in every context (REQ-287 AC4): U+2014. */
export const NO_VALUE = '—';
/** The change sentinel of API §12 / ENGINE §3 — rendered verbatim. */
export const NEW_VALUE = 'new';

const MINUS = '−';
const EN_DASH = '–';
/** U+2009 thin space and U+202F narrow no-break space — never in our output (README rule 4). */
const THIN_SPACES = new RegExp('[\\u2009\\u202F]', 'g');

/** REQ-261 AC2 rows 1–11 (money, counts, percentages, ratios) and AC6 (dates in the account's calendar,
 *  clock times in the device's zone). Hedged `*.about` contexts do not exist: one rounding rule everywhere. */
export type FormatContext =
  | 'money.exact' // row 1 — always the currency's minor units
  | 'money.cost' // row 2 — minor units below 1,000; whole units from 1,000
  | 'ratio.roas' // row 3 — "$X back per $1", two decimals
  | 'money.perDay' // row 4 — user-set amounts: whole when whole, else minor units (amount only)
  | 'money.headline' // row 5 — whole units (plan, pacing, projections; sentences from 100 upward)
  | 'money.sentence' // row 6 / 5 — an unpinned money param: minor units below 100, whole from 100
  | 'money.compact' // row 7 — whole below 10,000; from 10,000 one decimal + k / M, trailing .0 dropped
  | 'money.input' // row 12 — the amount INSIDE a number field: minor units at most, no grouping, no symbol; empty for null
  | 'count' // row 8 — grouped integer
  | 'count.compact' // row 8 — as money.compact without the currency
  | 'pct.rate' // row 9 — two decimals
  | 'pct.change' // row 10 — whole percent; "new" allowed
  | 'pct.share' // row 10 — whole percent
  | 'ratio.frequency' // row 11 — one decimal
  | 'date.day' // AC6 — "Wed, Sep 16" (a calendar date in the account's zone)
  | 'date.short' // AC6 — "Sep 16"
  | 'date.weekday' // AC6 — "Tue 15": the weekday and the day, no month (chart x-axes ≤ 14 days, D-143)
  | 'date.range' // AC6 — "Sep 15 – 21", "Aug 23 – Sep 21", one day "Sep 22"
  | 'time.clock'; // AC6 — "4:40 pm", an instant in the DEVICE zone

export const FORMAT_CONTEXTS: readonly FormatContext[] = [
  'money.exact',
  'money.cost',
  'ratio.roas',
  'money.perDay',
  'money.headline',
  'money.sentence',
  'money.compact',
  'money.input',
  'count',
  'count.compact',
  'pct.rate',
  'pct.change',
  'pct.share',
  'ratio.frequency',
  'date.day',
  'date.short',
  'date.weekday',
  'date.range',
  'time.clock',
];

/** What each context accepts. Numbers are unrounded (or six-decimal recorded facts); money is in major units of
 *  the account's currency; percentages are percent numbers (`1.39` = 1.39 %); a date is a `YYYY-MM-DD`
 *  calendar date in the account's zone; an instant is an ISO `…Z` string or epoch milliseconds. */
export interface FormatValueByContext {
  'money.exact': number | null;
  'money.cost': number | null;
  'ratio.roas': number | null;
  'money.perDay': number | null;
  'money.headline': number | null;
  'money.sentence': number | null;
  'money.compact': number | null;
  'money.input': number | null;
  count: number | null;
  'count.compact': number | null;
  'pct.rate': number | null;
  'pct.change': number | null | typeof NEW_VALUE;
  'pct.share': number | null;
  'ratio.frequency': number | null;
  'date.day': IsoDate | null;
  'date.short': IsoDate | null;
  'date.weekday': IsoDate | null;
  'date.range': DateRange | null;
  'time.clock': IsoInstant | number | null;
}

/** The two account facts formatting needs (the `AccountInfo` DTO of API §12 has both). */
export interface FormatAccount {
  /** ISO 4217 code — decides the symbol and the minor units (USD 2, JPY 0, KWD 3). Never converted. */
  currency: string;
  /** IANA zone of the ad account — the zone every calendar date is already in (REQ-261 AC3). */
  timeZone: string;
}

/** Everything a screen needs to format, gathered once. */
export interface FormatEnv {
  account: FormatAccount;
  /** BCP 47 tag of the user (`navigator.language` / the RN locale). */
  locale: string;
  /** IANA zone of the device (REQ-261 AC4) — required by `time.clock`. Passed in, never read here. */
  deviceTimeZone?: string;
}

/**
 * `format(value, context, account, locale, deviceTimeZone?)` — REQ-261 AC2 / AC6.
 * `null` → "—". Throws `RangeError` on a value the context cannot take (a NaN, a malformed date, an instant
 * without a device zone) so a bug shows in development instead of printing "NaN".
 */
export function format<C extends FormatContext>(
  value: FormatValueByContext[C],
  context: C,
  account: FormatAccount,
  locale: string,
  deviceTimeZone?: string,
): string {
  // Row 12: an empty FIELD is the empty string — a "—" or a "0" in a budget box reads as an amount (D-166).
  if (context === 'money.input' && (value === null || value === undefined)) return '';
  if (value === null || value === undefined) return NO_VALUE;
  if (value === NEW_VALUE) return NEW_VALUE;
  switch (context) {
    case 'money.exact':
    case 'money.cost':
    case 'money.perDay':
    case 'money.headline':
    case 'money.sentence':
      return formatMoney(asNumber(value, context), context, account.currency, locale);
    case 'money.compact':
      return formatCompact(asNumber(value, context), context, account.currency, locale);
    case 'money.input':
      return formatInput(asNumber(value, context), account.currency, locale);
    case 'count.compact':
      return formatCompact(asNumber(value, context), context, null, locale);
    case 'count':
      return formatDecimal(asNumber(value, context), 0, locale);
    case 'ratio.frequency':
      return formatDecimal(asNumber(value, context), 1, locale);
    case 'ratio.roas':
      return formatRoas(asNumber(value, context), account.currency, locale);
    case 'pct.rate':
      return formatPercent(asNumber(value, context), 2, locale);
    case 'pct.change':
    case 'pct.share':
      return formatPercent(asNumber(value, context), 0, locale);
    case 'date.day':
      return joinParts(calendarFormatter(locale, DATE_DAY).formatToParts(calendarDate(value)));
    case 'date.short':
      return joinParts(calendarFormatter(locale, DATE_SHORT).formatToParts(calendarDate(value)));
    case 'date.weekday':
      return formatWeekdayDay(calendarDate(value), locale);
    case 'date.range':
      return formatDateRange(value as DateRange, locale);
    case 'time.clock':
      return formatClock(value as IsoInstant | number, locale, deviceTimeZone);
    default:
      throw new RangeError(`format: unknown context "${String(context)}"`);
  }
}

/** A formatter bound to one screen's environment: `const fmt = createFormatter(env); fmt(spend, 'money.exact')`. */
export function createFormatter(
  env: FormatEnv,
): <C extends FormatContext>(value: FormatValueByContext[C], context: C) => string {
  return (value, context) => format(value, context, env.account, env.locale, env.deviceTimeZone);
}

// ---- numbers ---------------------------------------------------------------------------------------------

function asNumber(value: unknown, context: string): number {
  if (typeof value !== 'number' || !Number.isFinite(value))
    throw new RangeError(`format: ${context} needs a finite number, got ${String(value)}`);
  return value;
}

type MoneyContext =
  'money.exact' | 'money.cost' | 'money.perDay' | 'money.headline' | 'money.sentence';

/** How many decimals a money context shows for `value` (thresholds are tested on the UNROUNDED value — REQ-261
 *  AC2 conventions; `999.995` is below 1,000 and keeps its cents, becoming `$1,000.00`). */
function moneyDecimals(value: number, context: MoneyContext, minorUnits: number): number {
  const magnitude = Math.abs(value);
  switch (context) {
    case 'money.exact':
      return minorUnits;
    case 'money.cost':
      return magnitude < 1000 ? minorUnits : 0;
    case 'money.sentence':
      return magnitude < 100 ? minorUnits : 0;
    case 'money.headline':
      return 0;
    case 'money.perDay':
      // "Whole" is about the value, not how it was written: 50.00 is whole; 12.5 is not.
      return Number.isInteger(roundHalfAway(value, minorUnits)) ? 0 : minorUnits;
  }
}

function formatMoney(
  value: number,
  context: MoneyContext,
  currency: string,
  locale: string,
): string {
  const dp = moneyDecimals(value, context, minorUnitsOf(currency, locale));
  return joinParts(currencyFormatter(locale, currency, dp).formatToParts(roundHalfAway(value, dp)));
}

/**
 * REQ-261 AC2 row 12 (D-166) — the amount INSIDE a number field, seeded or being typed: at most the currency's
 * minor units, at least none (a whole amount seeds "50", never "50.00"), **no grouping**, the locale's own
 * decimal separator, no symbol (the field's adornment is that), and an ASCII minus should a value ever be
 * negative — this is text a keypad has to be able to type back. Rounded once, like every row, on the value we
 * hold; a value the USER typed with too many decimals never reaches here (`parseMoneyInput` refuses it).
 */
function formatInput(value: number, currency: string, locale: string): string {
  const dp = minorUnitsOf(currency, locale);
  const parts = numberFormatter(
    locale,
    { useGrouping: false, minimumFractionDigits: 0, maximumFractionDigits: dp },
    `input${dp}`,
  ).formatToParts(roundHalfAway(value, dp));
  let out = '';
  for (const part of parts) out += part.type === 'minusSign' ? '-' : part.value;
  return out;
}

/** What `parseMoneyInput` answers: the amount (or `null` for an empty field), or why the text is refused. */
export type MoneyInputParse =
  | { readonly ok: true; readonly value: number | null }
  | {
      readonly ok: false;
      readonly reason: 'invalid' | 'too_precise';
      readonly maxDecimals: number;
    };

/** The locale's own grouping and decimal symbols, read off `Intl` once per locale. */
function numberSymbols(locale: string): { group: string; decimal: string } {
  const cached = symbols.get(locale);
  if (cached) return cached;
  const parts = numberFormatter(locale, { useGrouping: true }, 'symbols').formatToParts(-12345.6);
  const found = {
    group: parts.find((p) => p.type === 'group')?.value ?? ',',
    decimal: parts.find((p) => p.type === 'decimal')?.value ?? '.',
  };
  symbols.set(locale, found);
  return found;
}
const symbols = new Map<string, { group: string; decimal: string }>();

/**
 * Row 12's other direction (D-166): the text in a number field → the amount. The locale's grouping symbol
 * and any whitespace are ignored (a pasted "4,000" is 4000; the field never SHOWS grouping, but a reader may
 * type it), the locale's decimal symbol is the decimal point, anything else that is not a figure is
 * `invalid`, an empty field is `null`, and a value with more fraction digits than the currency's minor units
 * is `too_precise` — refused, never rounded: "round once, at display" governs figures we derive, not a figure
 * the user typed. The sign is accepted; whether a negative amount is allowed is the field's own rule.
 */
export function parseMoneyInput(
  text: string,
  account: FormatAccount,
  locale: string,
): MoneyInputParse {
  const maxDecimals = minorUnitsOf(account.currency, locale);
  const { group, decimal } = numberSymbols(locale);
  const stripped = text
    .replace(/[\s\u00a0\u202f]/g, '')
    .split(group)
    .join('');
  if (stripped === '') return { ok: true, value: null };
  const normalised = decimal === '.' ? stripped : stripped.split(decimal).join('.');
  const match = /^(-?)(\d*)(?:\.(\d*))?$/.exec(normalised);
  if (!match || (match[2] === '' && (match[3] ?? '') === '')) {
    return { ok: false, reason: 'invalid', maxDecimals };
  }
  const fraction = match[3] ?? '';
  if (fraction.length > maxDecimals) return { ok: false, reason: 'too_precise', maxDecimals };
  const value = Number(`${match[1]}${match[2] || '0'}.${fraction || '0'}`);
  if (!Number.isFinite(value)) return { ok: false, reason: 'invalid', maxDecimals };
  return { ok: true, value };
}

/** REQ-261 AC2 row 7's threshold: below it a compact value is the grouped whole number; from it, a unit (k / M). */
const COMPACT_WHOLE_BELOW = 10_000;
/** The unit's own threshold: from 1,000 Intl's compact notation has a symbol to give (`$5k`), below it there is none. */
const COMPACT_UNIT_FROM = 1_000;

/** Row 7 / row 8 compact: below 10,000 (unrounded) the grouped whole number; from 10,000 one decimal in the
 *  unit `Intl` names (k / M / …), rounded FIRST so the unit can roll over (999,950 → 1.0 k·k → `$1M`). The
 *  value handed to `Intl` is exact at one decimal in its unit, so `Intl` only picks the symbol and the
 *  separators; a trailing `.0` is dropped by `minimumFractionDigits: 0`. */
function formatCompact(
  value: number,
  context: 'money.compact' | 'count.compact',
  currency: string | null,
  locale: string,
  wholeBelow = COMPACT_WHOLE_BELOW,
): string {
  const magnitude = Math.abs(value);
  if (magnitude < wholeBelow) {
    return currency === null
      ? formatDecimal(value, 0, locale)
      : joinParts(currencyFormatter(locale, currency, 0).formatToParts(roundHalfAway(value, 0)));
  }
  let exponent = 3;
  let scaled = roundHalfAway(shiftExponent(magnitude, -exponent), 1);
  while (scaled >= 1000 && exponent < 12) {
    exponent += 3;
    scaled = roundHalfAway(shiftExponent(magnitude, -exponent), 1);
  }
  const rounded = Math.sign(value) * shiftExponent(scaled, exponent);
  const options: Intl.NumberFormatOptions =
    currency === null
      ? { notation: 'compact', minimumFractionDigits: 0, maximumFractionDigits: 1 }
      : {
          style: 'currency',
          currency,
          notation: 'compact',
          minimumFractionDigits: 0,
          maximumFractionDigits: 1,
        };
  return joinParts(numberFormatter(locale, options, `${context}`).formatToParts(rounded));
}

/**
 * T-311 — THE AXIS RULE (DS §4.5; REQ-261 AC2 row 7's "chart axes" cell): ONE form per axis, the form its
 * largest tick takes. Row 7 is a rule per VALUE (whole below 10,000; a unit from 10,000) and an axis is three
 * values, so an axis topping at $10,000 read `$0 · $5,000 · $10k` — two forms of one magnitude beside each other,
 * visible on the live account. Here the largest tick decides: if it takes a unit, every tick ≥ 1,000 takes the
 * unit too (`$0 · $5k · $10k`; `$0 · $125k · $250k`; `$0 · $1M · $2M`); if it is whole, every tick is whole
 * (`$0 · $500 · $1,000`; `$0 · $2,500 · $5,000`). A tick below 1,000 on a unit axis stays a whole number (Intl has
 * no unit to give it, and `yTicks` never produces one beside a 10,000 top). Per-value `money.compact` is unchanged
 * — row 7 still governs a map label or a tile; this is the rule for a set of ticks, and only for ticks.
 */
export function formatTicks(
  values: readonly number[],
  context: 'money.compact' | 'count.compact',
  account: FormatAccount,
  locale: string,
): string[] {
  const currency = context === 'money.compact' ? account.currency : null;
  const top = Math.max(0, ...values.map((v) => Math.abs(v)));
  const wholeBelow = top >= COMPACT_WHOLE_BELOW ? COMPACT_UNIT_FROM : COMPACT_WHOLE_BELOW;
  return values.map((v) => formatCompact(v, context, currency, locale, wholeBelow));
}

function formatDecimal(value: number, dp: number, locale: string): string {
  const options: Intl.NumberFormatOptions = {
    minimumFractionDigits: dp,
    maximumFractionDigits: dp,
  };
  return joinParts(
    numberFormatter(locale, options, `decimal${dp}`).formatToParts(roundHalfAway(value, dp)),
  );
}

/** A percent number (`1.385` = 1.385 %) rounded to `dp` decimals, then scaled by 10⁻² through the exponent so
 *  `Intl`'s percent style multiplies an exact decimal back (`0.0139` → `1.39%` / `1,39 %`). */
function formatPercent(value: number, dp: number, locale: string): string {
  const rounded = roundHalfAway(value, dp);
  const options: Intl.NumberFormatOptions = {
    style: 'percent',
    minimumFractionDigits: dp,
    maximumFractionDigits: dp,
  };
  return joinParts(
    numberFormatter(locale, options, `percent${dp}`).formatToParts(shiftExponent(rounded, -2)),
  );
}

/** Row 3: "$4.15 back per $1" — the return at two decimals in the account's currency, and one unit of it. The
 *  words are English until the message catalogue (T-020) owns the phrase; the two amounts are locale-formatted. */
function formatRoas(value: number, currency: string, locale: string): string {
  const back = joinParts(
    currencyFormatter(locale, currency, 2).formatToParts(roundHalfAway(value, 2)),
  );
  const one = joinParts(currencyFormatter(locale, currency, 0).formatToParts(1));
  return `${back} back per ${one}`;
}

// ---- dates and times -------------------------------------------------------------------------------------

const DATE_DAY: Intl.DateTimeFormatOptions = { weekday: 'short', month: 'short', day: 'numeric' };
const DATE_SHORT: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
const CLOCK: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit' };

/** A calendar date as the instant `Date.UTC(y, m−1, d)`, to be rendered with `timeZone: 'UTC'` — the day never
 *  shifts, whatever zone the device or the account is in (`fmt-day-is-a-calendar-date`, `fmt-day-dst-end`). */
function calendarDate(iso: unknown): Date {
  if (typeof iso !== 'string')
    throw new RangeError(`format: a date context needs a YYYY-MM-DD string`);
  const { year, month, day } = parseIsoDate(iso);
  return new Date(Date.UTC(year, month - 1, day));
}

/**
 * "Tue 15" — the weekday and the day of the month, no month name (DESIGN_SYSTEM §4.5 as ruled in design QA
 * pass 1: a chart window of 14 days or fewer labels its x-axis by weekday, because on a seven-day chart the
 * weekday *is* the information and "Sep" seven times carries none).
 *
 * `{ weekday: 'short', day: 'numeric' }` on its own is NOT it: ICU's en-US skeleton for that pair is day-first
 * ("15 Tue"). So the parts come from the `date.day` recipe — the locale's own weekday/day order, already
 * proven by `fmt-day-*` — and the MONTH part is dropped, together with the one literal that separated it from
 * the rest (the literal after it when the month comes first, the literal before it otherwise). Parts are
 * selected, never edited: this is the same mechanism `joinParts` exists for (README rule 4).
 * en-US "Tue 15" · en-GB "Tue, 15" · fr-FR "mar. 15" · de-DE "Di., 15" · ja-JP "15日(火)".
 */
function formatWeekdayDay(date: Date, locale: string): string {
  const parts = calendarFormatter(locale, DATE_DAY).formatToParts(date);
  const month = parts.findIndex((p) => p.type === 'month');
  if (month < 0) return joinParts(parts);
  const separator = month === 0 ? month + 1 : month - 1;
  const kept = parts.filter(
    (_, i) => i !== month && !(i === separator && parts[i]?.type === 'literal'),
  );
  return joinParts(kept).trim();
}

function formatDateRange(range: DateRange, locale: string): string {
  if (!range || typeof range !== 'object')
    throw new RangeError('format: date.range needs { since, until }');
  const since = calendarDate(range.since);
  const until = calendarDate(range.until);
  const formatter = calendarFormatter(locale, DATE_SHORT);
  // A one-day range ("Today", "Yesterday") is that day, not "Sep 22 – 22" (REQ-261 AC2 conventions).
  if (range.since === range.until) return joinParts(formatter.formatToParts(since));
  if (since.getTime() > until.getTime())
    throw new RangeError(`format: date.range is reversed (${range.since} > ${range.until})`);
  if (typeof formatter.formatRangeToParts === 'function')
    return joinParts(formatter.formatRangeToParts(since, until));
  // Engines without `formatRangeToParts` (older Hermes): the same three shapes ICU produces — one month
  // "Sep 15 – 21", two months "Aug 23 – Sep 21", and, as ICU does when the years differ, both years
  // ("Dec 28, 2025 – Jan 3, 2026"). The locale's own parts around an en dash; no zone.
  const sameYear = range.since.slice(0, 4) === range.until.slice(0, 4);
  const sameMonth = sameYear && range.since.slice(5, 7) === range.until.slice(5, 7);
  const whole = sameYear
    ? formatter
    : calendarFormatter(locale, { ...DATE_SHORT, year: 'numeric' });
  const end = sameMonth
    ? calendarFormatter(locale, { day: 'numeric' }).formatToParts(until)
    : whole.formatToParts(until);
  return `${joinParts(whole.formatToParts(since))} ${EN_DASH} ${joinParts(end)}`;
}

/** An instant on the DEVICE's wall clock, in the locale's own hour cycle (en-US 12-hour with a lower-cased
 *  day period, de-DE 24-hour with none). DST-safe by construction: `Intl` applies the zone's rules. */
function formatClock(
  value: IsoInstant | number,
  locale: string,
  deviceTimeZone: string | undefined,
): string {
  if (!deviceTimeZone)
    throw new RangeError(
      'format: time.clock needs the device time zone (pass it in; never read it here)',
    );
  const ms = typeof value === 'number' ? value : new Date(value).getTime();
  if (!Number.isFinite(ms)) throw new RangeError(`format: "${String(value)}" is not an instant`);
  return joinParts(dateTimeFormatter(locale, deviceTimeZone, CLOCK).formatToParts(new Date(ms)));
}

// ---- Intl plumbing ---------------------------------------------------------------------------------------

type Part = Intl.NumberFormatPart | Intl.DateTimeFormatPart | Intl.DateTimeRangeFormatPart;

/** The normalisation pipeline (design note 3) — applied per PART, then joined. */
function joinParts(parts: readonly Part[]): string {
  let out = '';
  for (const part of parts) {
    let text = part.value.replace(THIN_SPACES, ' ');
    switch (part.type) {
      case 'minusSign':
        text = MINUS;
        break;
      case 'dayPeriod':
        text = text.toLowerCase();
        break;
      case 'compact':
        if (text === 'K') text = 'k';
        break;
      default:
        break;
    }
    out += text;
  }
  return out;
}

const numberFormatters = new Map<string, Intl.NumberFormat>();
const dateFormatters = new Map<string, Intl.DateTimeFormat>();
const minorUnits = new Map<string, number>();

function numberFormatter(
  locale: string,
  options: Intl.NumberFormatOptions,
  key: string,
): Intl.NumberFormat {
  const id = `${locale}|${key}|${options.currency ?? ''}`;
  let f = numberFormatters.get(id);
  if (!f) {
    f = new Intl.NumberFormat(locale, options);
    numberFormatters.set(id, f);
  }
  return f;
}

function currencyFormatter(locale: string, currency: string, dp: number): Intl.NumberFormat {
  return numberFormatter(
    locale,
    { style: 'currency', currency, minimumFractionDigits: dp, maximumFractionDigits: dp },
    `currency${dp}`,
  );
}

/** The currency's minor units as `Intl` knows them (ISO 4217: USD 2, JPY 0, KWD 3). */
function minorUnitsOf(currency: string, locale: string): number {
  const id = `${locale}|${currency}`;
  let n = minorUnits.get(id);
  if (n === undefined) {
    // Typed optional in lib.d.ts; a currency-style formatter always resolves it (2 when the code is unknown).
    n =
      numberFormatter(locale, { style: 'currency', currency }, 'currencyDefault').resolvedOptions()
        .maximumFractionDigits ?? 2;
    minorUnits.set(id, n);
  }
  return n;
}

function dateTimeFormatter(
  locale: string,
  timeZone: string,
  options: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormat {
  const id = `${locale}|${timeZone}|${JSON.stringify(options)}`;
  let f = dateFormatters.get(id);
  if (!f) {
    f = new Intl.DateTimeFormat(locale, { ...options, timeZone });
    dateFormatters.set(id, f);
  }
  return f;
}

/** Calendar dates render in UTC by construction (design note 4). */
function calendarFormatter(
  locale: string,
  options: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormat {
  return dateTimeFormatter(locale, 'UTC', options);
}
